import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ShoppingCart,
  Menu,
  X,
  Package,
  Boxes,
  User,
  Users,
  Settings,
  LogOut,
  Globe,
  AlertTriangle,
  Mail,
  MapPin,
  Store,
  Sparkles,
  Star,
  Tag,
  HelpCircle,
  FileText,
  Shield,
  Info,
  Search,
} from "lucide-react";
import { Instagram, ExternalLink } from "lucide-react";
import { FaTiktok, FaWhatsapp } from "react-icons/fa";
import CurrencySelector from "./CurrencySelector";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useAuthContext } from "@/lib/AuthProvider";
import { useUserProfile } from "@/context/UserProfileContext";
import { useBanner } from "@/context/BannerContext";
import { useCart } from "@/context/CartContext";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import AuthModalWrapper from "./AuthModalWrapper";
import Banner from "./Banner";
import { useIsMobile } from "@/hooks/use-mobile";

interface NavBarProps {
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
}

const NAVBAR_MOBILE_BREAKPOINT = 1270;

const NavBar = ({ mobileMenuOpen, setMobileMenuOpen }: NavBarProps) => {
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [authTab, setAuthTab] = useState<"login" | "register">("login");
  const [totalBannerHeight, setTotalBannerHeight] = useState(0);
  const bannerContainerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Auth context
  const { user, logout, isGoogleUser } = useAuthContext();
  
  // User profile context
  const { userProfile } = useUserProfile();
  
  // Banner context
  const { bannerConfig, loading: bannerLoading, isBannerDismissed, dismissedBanners } = useBanner();
  
  // Cart context
  const { getCartCount } = useCart();
  const cartItemsCount = getCartCount();

  // Helper function to get display name
  const getDisplayName = () => {
    if (!user) return '';
    
    // For Google users, use displayName or email prefix as fallback
    if (isGoogleUser()) {
      return user.displayName || user.email?.split("@")[0] || 'User';
    }
    
    // For email/password users, use name from userProfile or email prefix as fallback
    return userProfile?.name || user.email?.split("@")[0] || 'User';
  };

  // Helper function to get avatar initials
  const getAvatarInitials = () => {
    if (!user) return '';
    
    // For Google users, use displayName or email
    if (isGoogleUser()) {
      return user.displayName?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase() || 'U';
    }
    
    // For email/password users, use name from userProfile or email
    if (userProfile?.name) {
      return userProfile.name.charAt(0).toUpperCase();
    }
    
    return user.email?.charAt(0).toUpperCase() || 'U';
  };

  // Dynamic height measurement using ResizeObserver
  useEffect(() => {
    // Don't measure height until banners have finished loading
    if (bannerLoading) {
      return;
    }

    const measureBannerHeight = () => {
      // Check if any banners should be shown
      const hasBanner1 = bannerConfig.banner1?.isActive && !isBannerDismissed('banner1');
      const hasBanner2 = bannerConfig.banner2?.isActive && !isBannerDismissed('banner2');
      
      // Determine if navbar should contribute to height
      // On mobile (<= NAVBAR_MOBILE_BREAKPOINT) and not home page, navbar is hidden
      const isMobileWidth = window.innerWidth <= NAVBAR_MOBILE_BREAKPOINT;
      const navbarHeight = (isMobileWidth && !isHomePage()) ? 0 : 112;
      
      if (!hasBanner1 && !hasBanner2) {
        // No banners to show, set height to 0
        setTotalBannerHeight(0);
        // Dispatch event for Layout component (banners + navbar height)
        window.dispatchEvent(new CustomEvent('navHeightUpdate', { 
          detail: { height: navbarHeight } // Navbar height (or 0 if hidden on mobile)
        }));
        return;
      }
      
      if (bannerContainerRef.current) {
        const height = bannerContainerRef.current.offsetHeight;
        setTotalBannerHeight(height);
        // Dispatch event for Layout component (banners + navbar height)
        window.dispatchEvent(new CustomEvent('navHeightUpdate', { 
          detail: { height: height + navbarHeight } // Banner height + navbar height (or 0 if hidden)
        }));
      }
    };

    // Initial measurement with delay to ensure DOM is updated
    const timeoutId = setTimeout(measureBannerHeight, 50);

    // Set up ResizeObserver to watch for banner changes
    const resizeObserver = new ResizeObserver(() => {
      // Add small delay to ensure transitions complete
      setTimeout(measureBannerHeight, 50);
    });
    
    if (bannerContainerRef.current) {
      resizeObserver.observe(bannerContainerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
      clearTimeout(timeoutId);
    };
  }, [bannerConfig, bannerLoading, user, location.pathname, dismissedBanners]);

  // Add window resize listener to handle mobile/desktop transitions
  useEffect(() => {
    const handleResize = () => {
      // Re-measure height when window size changes
      const hasBanner1 = bannerConfig.banner1?.isActive && !isBannerDismissed('banner1');
      const hasBanner2 = bannerConfig.banner2?.isActive && !isBannerDismissed('banner2');
      
      const isMobile = window.innerWidth <= NAVBAR_MOBILE_BREAKPOINT;
      const navbarHeight = (isMobile && !isHomePage()) ? 0 : 112;
      
      if (!hasBanner1 && !hasBanner2) {
        window.dispatchEvent(new CustomEvent('navHeightUpdate', { 
          detail: { height: navbarHeight }
        }));
      } else if (bannerContainerRef.current) {
        const height = bannerContainerRef.current.offsetHeight;
        window.dispatchEvent(new CustomEvent('navHeightUpdate', { 
          detail: { height: height + navbarHeight }
        }));
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [bannerConfig, user, location.pathname, dismissedBanners]);

  // Initial height dispatch while banners are loading
  useEffect(() => {
    if (bannerLoading) {
      // Determine if navbar should contribute to height
      const isMobile = window.innerWidth <= NAVBAR_MOBILE_BREAKPOINT;
      const navbarHeight = (isMobile && !isHomePage()) ? 0 : 112;
      
      // Dispatch initial navbar-only height while banners load
      window.dispatchEvent(new CustomEvent('navHeightUpdate', { 
        detail: { height: navbarHeight } // Navbar height (or 0 if hidden on mobile)
      }));
    }
  }, [bannerLoading, location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleSignOut = async () => {
    try {
      await logout();
    } catch (err) {
      // Silent error handling
    }
  };

  const navigateToSettings = () => {
    if (user) {
      navigate("/settings");
      setMobileMenuOpen(false);
    } else {
      openAuthModal("login");
      setMobileMenuOpen(false);
    }
  };

  const navigateToOrders = () => {
    if (user) {
      navigate("/orders");
    } else {
      openAuthModal("login");
    }
  };

  const navigateToAddresses = () => {
    if (user) {
      navigate("/settings?tab=addresses");
    } else {
      openAuthModal("login");
    }
  };

  const navigateToAffiliate = () => {
    if (user) {
      navigate("/affiliate");
    } else {
      openAuthModal("login");
    }
  };

  const navigateToSearch = () => {
    navigate("/products", { 
      state: { autoFocusSearch: true } 
    });
  };

  const openAuthModal = (tab: "login" | "register") => {
    setAuthTab(tab);
    setAuthModalOpen(true);
  };

  // Helper function to determine if any banners should be shown
  const shouldShowBanners = () => {
    const hasBanner1 = bannerConfig.banner1?.isActive && !isBannerDismissed('banner1');
    const hasBanner2 = bannerConfig.banner2?.isActive && !isBannerDismissed('banner2');
    return hasBanner1 || hasBanner2;
  };

  // Helper function to check if current page is home page
  const isHomePage = () => {
    return location.pathname === "/" || location.pathname === "/home";
  };

  const navItems = [
    {
      name: "Shop All",
      href: "/products",
    },
    {
      name: "Combos",
      href: "/bundles",
    },
    {
      name: "New Arrivals",
      href: "/products/new",
    },
  {
      name: "Best Sellers",
      href: "/products/best-sellers",
    },
    {
      name: "Special Offers",
      href: "/products/on-sale",
    },
  ];

  const drawerNavIconMap: Record<string, React.ElementType> = {
    "Shop All": Store,
    "Combos": Boxes,
    "New Arrivals": Sparkles,
    "Best Sellers": Star,
    "Special Offers": Tag,
  };

  const drawerLinks = [
    ...navItems.map(item => ({
      key: item.name,
      label: item.name,
      to: item.href,
      icon: drawerNavIconMap[item.name] || Store,
      isActive: isActive(item.href)
    })),
    ...(user && userProfile?.isAffiliate ? [{
      key: "affiliate",
      label: "Affiliate Dashboard",
      to: "/affiliate",
      icon: Users,
      isActive: location.pathname === "/affiliate"
    }] : []),
    ...(user ? [{
      key: "addresses",
      label: "Addresses",
      to: "/settings?tab=addresses",
      icon: MapPin,
      isActive: location.pathname.includes("/settings") && location.search.includes("addresses")
    }, {
      key: "orders",
      label: "Orders",
      to: "/orders",
      icon: Package,
      isActive: location.pathname === "/orders"
    }] : [])
  ];

  const renderDrawerLink = (
    link: {
      key: string;
      label: string;
      to: string;
      icon: React.ElementType;
      isActive: boolean;
    }
  ) => {
    const IconComponent = link.icon;
    return (
      <Link
        key={link.key}
        to={link.to}
        className={cn(
          "flex items-center mx-2 rounded-lg transition-all duration-300 relative",
          link.isActive
            ? "text-jamaica-green bg-jamaica-green/10 border border-jamaica-green/20 shadow-sm"
            : "text-gray-700 hover:text-jamaica-green hover:bg-gray-50/80 hover:shadow-sm"
        )}
        style={{
          padding: `var(--drawer-spacing) var(--drawer-padding)`,
          fontSize: `max(0.5rem, min(0.875rem, ${window.innerHeight * 0.014}px))`
        }}
        onClick={() => {
          setTimeout(() => setMobileMenuOpen(false), 150);
        }}
      >
        <IconComponent
          style={{
            width: `max(0.75rem, min(1rem, ${window.innerHeight * 0.016}px))`,
            height: `max(0.75rem, min(1rem, ${window.innerHeight * 0.016}px))`,
            marginRight: `var(--drawer-spacing)`
          }}
        />
        <span className="truncate">{link.label}</span>
      </Link>
    );
  };

  return (
    <>
      {/* Banner System - Always render container for ref, but conditionally show content */}
      <div ref={bannerContainerRef} className="fixed top-0 left-0 right-0 z-50 block mobile-safe-area">
        {/* Banner 1 */}
        {bannerConfig.banner1?.isActive && !isBannerDismissed('banner1') && (
          <Banner banner={bannerConfig.banner1} />
        )}
        
        {/* Banner 2 */}
        {bannerConfig.banner2?.isActive && !isBannerDismissed('banner2') && (
          <Banner banner={bannerConfig.banner2} />
        )}
        
      </div>

      {/* Main Navigation - Position based on banner presence */}
      <nav
        className={cn(
          "w-full transition-all duration-500 ease-in-out fixed z-40 mobile-fixed-nav",
          // Hide navbar on mobile except on home page
          !isHomePage() && isMobile && "hidden",
          scrolled
            ? "bg-white/90 backdrop-blur-lg shadow-md py-2"
            : "bg-white/60 backdrop-blur-sm py-4"
        )}
        style={{ top: `${totalBannerHeight}px` }}
      >
        {/* Auth Modal */}
        <AuthModalWrapper
          isOpen={authModalOpen}
          onOpenChange={setAuthModalOpen}
          defaultTab={authTab}
        />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Left side - Navigation links */}
            {!isMobile && (
              <div className="flex items-center flex-1 justify-start pr-10">
              <NavigationMenu className="w-full max-w-[360px]">
                <NavigationMenuList className="w-full justify-between gap-1">
                  {navItems.map((item) => (
                    <NavigationMenuItem key={item.name}>
                      <NavigationMenuLink
                        asChild
                        className={cn(
                          navigationMenuTriggerStyle(),
                          "font-medium tracking-wide navbar-nav-text",
                          isActive(item.href)
                            ? "text-jamaica-green after:scale-x-100"
                            : "text-gray-700"
                        )}
                      >
                        <Link to={item.href}>
                          {item.name}
                        </Link>
                      </NavigationMenuLink>
                    </NavigationMenuItem>
                  ))}
                </NavigationMenuList>
              </NavigationMenu>
              </div>
            )}

            {/* Center - Logo - Removed hover animation */}
            <div className="flex justify-center items-center flex-1 absolute left-1/2 transform -translate-x-1/2">
              <Link
                to="/"
                className="flex-shrink-0 flex items-center justify-center"
              >
                <img
                  src="/lovable-uploads/bf2869c6-c7dd-4a46-a046-7189f9435aa3.png"
                  alt="JamPack'd Logo"
                  className={cn(
                    "h-32 w-auto object-contain transition-all duration-500",
                    scrolled ? "scale-75" : "scale-100"
                  )}
                />
              </Link>
            </div>

            {/* Right side - Icons */}
            {!isMobile && (
              <div className="flex items-center space-x-4 flex-1 justify-end pl-10">
              {/* Affiliate Icon - Only shown for approved affiliates */}
              {user && userProfile?.isAffiliate && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="rounded-full bg-gray-50/80 hover:bg-gray-100/90 transition-all duration-300 hover:scale-110 group"
                        onClick={navigateToAffiliate}
                      >
                        <Users className="h-5 w-5 text-gray-600 group-hover:text-jamaica-green transition-colors" />
                        <span className="sr-only">Affiliate Dashboard</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Affiliate Dashboard</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {/* Search Icon - Always visible */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="rounded-full bg-gray-50/80 hover:bg-gray-100/90 transition-all duration-300 hover:scale-110 group"
                      onClick={() => navigateToSearch()}
                    >
                      <Search className="h-5 w-5 text-gray-600 group-hover:text-jamaica-green transition-colors" />
                      <span className="sr-only">Search Products</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Search Products</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {/* Addresses Icon - Only shown when authenticated */}
              {user && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="rounded-full bg-gray-50/80 hover:bg-gray-100/90 transition-all duration-300 hover:scale-110 group"
                        onClick={navigateToAddresses}
                      >
                        <MapPin className="h-5 w-5 text-gray-600 group-hover:text-jamaica-green transition-colors" />
                        <span className="sr-only">Addresses</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Saved Addresses</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {/* Orders Icon - Only shown when authenticated */}
              {user && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="rounded-full bg-gray-50/80 hover:bg-gray-100/90 transition-all duration-300 hover:scale-110 group"
                        onClick={navigateToOrders}
                      >
                        <Package className="h-5 w-5 text-gray-600 group-hover:text-jamaica-green transition-colors" />
                        <span className="sr-only">Orders</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Your Orders</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {/* Cart Icon */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link to="/cart">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="rounded-full bg-gray-50/80 hover:bg-gray-100/90 transition-all duration-300 hover:scale-110 relative group"
                      >
                        <ShoppingCart className="h-5 w-5 text-gray-600 group-hover:text-jamaica-green transition-colors" />
                        <span className="sr-only">Cart</span>
                        {cartItemsCount > 0 && (
                          <Badge className="absolute -top-1 -right-2 bg-jamaica-yellow text-jamaica-black h-5 w-5 flex items-center justify-center p-0 text-xs font-bold rounded-full transform group-hover:scale-110 transition-transform">
                            {cartItemsCount}
                          </Badge>
                        )}
                      </Button>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Shopping Cart</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {/* Profile section - Show either login button or avatar */}
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                      <div className="flex items-center space-x-4 cursor-pointer transition-all duration-300 hover:scale-105 group px-3 py-1 rounded-md border border-jamaica-green hover:border-jamaica-yellow shadow-sm bg-white/80 hover:bg-white/95">
                        <Avatar className="h-8 w-8 shadow-sm transition-all duration-300">
                          <AvatarFallback className="bg-[#00742f] text-[#ffc901] font-bold text-sm">
                          {getAvatarInitials()}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm font-medium text-gray-700 group-hover:text-jamaica-green transition-colors pr-1">
                        {getDisplayName()}
                      </span>
                    </div>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-56 p-2 rounded-lg animate-in fade-in-50 zoom-in-95 duration-300 shadow-lg border border-gray-100"
                  >
                    <DropdownMenuItem
                      className="flex items-center gap-1 py-2 cursor-pointer rounded-md"
                      onClick={navigateToSettings}
                    >
                      <Settings className="h-4 w-4 text-gray-500" />
                      <span>Settings</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="flex items-center gap-1 py-2 cursor-pointer rounded-md text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={handleSignOut}
                    >
                      <LogOut className="h-4 w-4" />
                      <span>Sign out</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="rounded-full bg-gray-50/80 hover:bg-gray-100/90 transition-all duration-300 hover:scale-110"
                        onClick={() => openAuthModal("login")}
                      >
                        <User className="h-5 w-5 text-gray-600 hover:text-jamaica-green transition-colors" />
                        <span className="sr-only">Profile</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Profile</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              </div>
            )}

            {/* Mobile menu button */}
            {isMobile && (
              <div className="flex items-center">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                      className="rounded-full bg-gray-50/80 hover:bg-gray-100/90 transition-all duration-300"
                      size="icon"
                    >
                      <span className="sr-only">Open main menu</span>
                      {mobileMenuOpen ? (
                        <X
                          className="h-5 w-5 text-gray-600 hover:text-jamaica-green transition-colors"
                          aria-hidden="true"
                        />
                      ) : (
                        <Menu
                          className="h-5 w-5 text-gray-600 hover:text-jamaica-green transition-colors"
                          aria-hidden="true"
                        />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{mobileMenuOpen ? "Close Menu" : "Open Menu"}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile Drawer Overlay */}
      {isMobile && (
        <div
          className={cn(
            "fixed inset-0 z-[2147483000] transition-all duration-300 ease-in-out",
            mobileMenuOpen ? "visible" : "invisible"
          )}
        >
        {/* Backdrop with blur */}
        <div
          className={cn(
            "absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300",
            mobileMenuOpen ? "opacity-100" : "opacity-0"
          )}
          onClick={() => setMobileMenuOpen(false)}
        />

        {/* Floating drawer toggle (3D) on backdrop - visible only when open */}
        {mobileMenuOpen && (
          <div
            className="fixed top-4 z-[2147483645]"
            style={{ left: `calc(20rem + 0.5rem)` }}
          >
            <Button
              variant="ghost"
              size="icon"
              aria-label="Close menu"
              onClick={() => setMobileMenuOpen(false)}
              className="rounded-full bg-gray-50/80 hover:bg-gray-100/90 transition-all duration-300 shadow-xl border border-white/60 backdrop-blur-sm hover:scale-105"
            >
              <X className="h-5 w-5 text-gray-600 hover:text-jamaica-green transition-colors" />
            </Button>
          </div>
        )}
        
        {/* Drawer */}
        <div
          className={cn(
            "absolute top-0 left-0 h-[100vh] w-80 max-w-[85vw] bg-white/95 backdrop-blur-md shadow-2xl transform transition-transform duration-300 ease-in-out flex flex-col z-[2147483640]",
            mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
          )}
          style={{
            fontSize: `max(0.5rem, min(0.875rem, ${window.innerHeight * 0.016}px))`,
            '--drawer-spacing': `max(0.125rem, min(0.5rem, ${window.innerHeight * 0.008}px))`,
            '--drawer-padding': `max(0.25rem, min(0.75rem, ${window.innerHeight * 0.012}px))`
          } as React.CSSProperties}
        >
          {/* Close Button removed - floating toggle handles close */}

          {/* User Profile Section with Glass Morphism Background */}
          {user && (
            <div 
              className="bg-gradient-to-r from-jamaica-green/10 via-jamaica-yellow/10 to-jamaica-green/10 backdrop-blur-sm border-b border-jamaica-green/20 px-1.5 sm:px-2.5"
              style={{ padding: `calc(var(--drawer-padding) * 0.75) 1rem` }}
            >
              <div className="flex items-center" style={{ gap: `calc(var(--drawer-spacing) * 0.75)` }}>
                <Avatar 
                  className="border-2 border-jamaica-green shadow-md flex-shrink-0"
                  style={{ 
                    width: `max(1.25rem, min(2.25rem, ${window.innerHeight * 0.035}px))`,
                    height: `max(1.25rem, min(2.25rem, ${window.innerHeight * 0.035}px))`
                  }}
                >
                  <AvatarFallback 
                    className="bg-[#00742f] text-[#ffc901] font-bold"
                    style={{ fontSize: `max(0.4rem, min(0.75rem, ${window.innerHeight * 0.011}px))` }}
                  >
                    {getAvatarInitials()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div 
                    className="font-semibold text-gray-800 truncate"
                    style={{ fontSize: `max(0.6rem, min(1rem, ${window.innerHeight * 0.018}px))` }}
                  >
                    {getDisplayName()}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Main Content Area - Uses flex to push footer to bottom */}
          <div className="flex-1 flex flex-col min-h-0">
            <div 
              className="flex-1 px-1 overflow-hidden"
              style={{ 
                paddingTop: `var(--drawer-spacing)`,
                paddingBottom: `var(--drawer-spacing)`,
                gap: `var(--drawer-spacing)`
              }}
            >

              {/* Navigation Items with Icons */}
              <div className="flex flex-col" style={{ gap: `var(--drawer-spacing)` }}>
                {drawerLinks.map(link => renderDrawerLink(link))}
              </div>

              {/* Info Pages */}
              <div 
                className="border-t border-gray-200 flex flex-col"
                style={{ 
                  paddingTop: `var(--drawer-spacing)`,
                  marginTop: `var(--drawer-spacing)`,
                  gap: `var(--drawer-spacing)`
                }}
              >
                <Link
                  to="/about"
                  className={cn(
                    "flex items-center mx-2 rounded-lg transition-all duration-300 relative",
                    location.pathname === "/about"
                      ? "text-jamaica-green bg-jamaica-green/10 border border-jamaica-green/20 shadow-sm"
                      : "text-gray-700 hover:text-jamaica-green hover:bg-gray-50/80 hover:shadow-sm"
                  )}
                  style={{
                    padding: `var(--drawer-spacing) var(--drawer-padding)`,
                    fontSize: `max(0.5rem, min(0.875rem, ${window.innerHeight * 0.014}px))`
                  }}
                  onClick={() => setTimeout(() => setMobileMenuOpen(false), 150)}
                >
                  <Info 
                    style={{
                      width: `max(0.75rem, min(1rem, ${window.innerHeight * 0.016}px))`,
                      height: `max(0.75rem, min(1rem, ${window.innerHeight * 0.016}px))`,
                      marginRight: `var(--drawer-spacing)`
                    }}
                  />
                  <span className="truncate">About Us</span>
                </Link>
                <Link
                  to="/faq"
                  className={cn(
                    "flex items-center mx-2 rounded-lg transition-all duration-300 relative",
                    location.pathname === "/faq"
                      ? "text-jamaica-green bg-jamaica-green/10 border border-jamaica-green/20 shadow-sm"
                      : "text-gray-700 hover:text-jamaica-green hover:bg-gray-50/80 hover:shadow-sm"
                  )}
                  style={{
                    padding: `var(--drawer-spacing) var(--drawer-padding)`,
                    fontSize: `max(0.5rem, min(0.875rem, ${window.innerHeight * 0.014}px))`
                  }}
                  onClick={() => setTimeout(() => setMobileMenuOpen(false), 150)}
                >
                  <HelpCircle 
                    style={{
                      width: `max(0.75rem, min(1rem, ${window.innerHeight * 0.016}px))`,
                      height: `max(0.75rem, min(1rem, ${window.innerHeight * 0.016}px))`,
                      marginRight: `var(--drawer-spacing)`
                    }}
                  />
                  <span className="truncate">FAQ</span>
                </Link>
                <Link
                  to="/terms"
                  className={cn(
                    "flex items-center mx-2 rounded-lg transition-all duration-300 relative",
                    location.pathname === "/terms"
                      ? "text-jamaica-green bg-jamaica-green/10 border border-jamaica-green/20 shadow-sm"
                      : "text-gray-700 hover:text-jamaica-green hover:bg-gray-50/80 hover:shadow-sm"
                  )}
                  style={{
                    padding: `var(--drawer-spacing) var(--drawer-padding)`,
                    fontSize: `max(0.5rem, min(0.875rem, ${window.innerHeight * 0.014}px))`
                  }}
                  onClick={() => setTimeout(() => setMobileMenuOpen(false), 150)}
                >
                  <FileText 
                    style={{
                      width: `max(0.75rem, min(1rem, ${window.innerHeight * 0.016}px))`,
                      height: `max(0.75rem, min(1rem, ${window.innerHeight * 0.016}px))`,
                      marginRight: `var(--drawer-spacing)`
                    }}
                  />
                  <span className="truncate">Terms of Service</span>
                </Link>
                <Link
                  to="/privacy"
                  className={cn(
                    "flex items-center mx-2 rounded-lg transition-all duration-300 relative",
                    location.pathname === "/privacy"
                      ? "text-jamaica-green bg-jamaica-green/10 border border-jamaica-green/20 shadow-sm"
                      : "text-gray-700 hover:text-jamaica-green hover:bg-gray-50/80 hover:shadow-sm"
                  )}
                  style={{
                    padding: `var(--drawer-spacing) var(--drawer-padding)`,
                    fontSize: `max(0.5rem, min(0.875rem, ${window.innerHeight * 0.014}px))`
                  }}
                  onClick={() => setTimeout(() => setMobileMenuOpen(false), 150)}
                >
                  <Shield 
                    style={{
                      width: `max(0.75rem, min(1rem, ${window.innerHeight * 0.016}px))`,
                      height: `max(0.75rem, min(1rem, ${window.innerHeight * 0.016}px))`,
                      marginRight: `var(--drawer-spacing)`
                    }}
                  />
                  <span className="truncate">Privacy Policy</span>
                </Link>

                {/* Become an Affiliate - only when not an affiliate */}
                {(!user || !userProfile?.isAffiliate) && (
                  <Link
                    to="/affiliate/signup"
                    className={cn(
                      "flex items-center mx-2 rounded-lg transition-all duration-300 relative",
                      location.pathname === "/affiliate/signup"
                        ? "text-jamaica-green bg-jamaica-green/10 border border-jamaica-green/20 shadow-sm"
                        : "text-gray-700 hover:text-jamaica-green hover:bg-gray-50/80 hover:shadow-sm"
                    )}
                    style={{
                      padding: `var(--drawer-spacing) var(--drawer-padding)`,
                      fontSize: `max(0.5rem, min(0.875rem, ${window.innerHeight * 0.014}px))`
                    }}
                    onClick={() => setTimeout(() => setMobileMenuOpen(false), 150)}
                  >
                    <Users 
                      style={{
                        width: `max(0.75rem, min(1rem, ${window.innerHeight * 0.016}px))`,
                        height: `max(0.75rem, min(1rem, ${window.innerHeight * 0.016}px))`,
                        marginRight: `var(--drawer-spacing)`
                      }}
                    />
                    <span className="truncate">Become an Affiliate</span>
                  </Link>
                )}
              </div>

              {/* Sign Out - Only shown when authenticated */}
              {user && (
                <div 
                  className="border-t border-gray-100/60"
                  style={{
                    paddingTop: `var(--drawer-spacing)`,
                    marginTop: `var(--drawer-spacing)`
                  }}
                >
                  <button
                    className="flex items-center mx-2 text-red-500 hover:text-red-600 hover:bg-red-50/50 rounded-lg transition-all duration-300 w-full text-left"
                    style={{
                      padding: `var(--drawer-spacing) var(--drawer-padding)`,
                      fontSize: `max(0.5rem, min(0.875rem, ${window.innerHeight * 0.014}px))`
                    }}
                    onClick={() => {
                      handleSignOut();
                      setMobileMenuOpen(false);
                    }}
                  >
                    <LogOut 
                      style={{
                        width: `max(0.75rem, min(1rem, ${window.innerHeight * 0.016}px))`,
                        height: `max(0.75rem, min(1rem, ${window.innerHeight * 0.016}px))`,
                        marginRight: `var(--drawer-spacing)`
                      }}
                    />
                    <span className="truncate">Sign out</span>
                  </button>
                </div>
              )}

              {/* Login Button - Only shown when not authenticated */}
              {!user && (
                <div 
                  className="mx-2"
                  style={{ paddingTop: `var(--drawer-padding)` }}
                >
                  <Button
                    className="w-full justify-center bg-jamaica-green text-white hover:bg-jamaica-green/90 rounded-xl font-medium"
                    style={{
                      padding: `var(--drawer-padding)`,
                      fontSize: `max(0.6rem, min(1rem, ${window.innerHeight * 0.016}px))`
                    }}
                    onClick={() => {
                      setTimeout(() => {
                        openAuthModal("login");
                        setMobileMenuOpen(false);
                      }, 150);
                    }}
                  >
                    <User 
                      style={{
                        width: `max(0.8rem, min(1.25rem, ${window.innerHeight * 0.018}px))`,
                        height: `max(0.8rem, min(1.25rem, ${window.innerHeight * 0.018}px))`,
                        marginRight: `var(--drawer-spacing)`
                      }}
                    />
                    <span className="truncate">Login / Sign Up</span>
                  </Button>
                </div>
              )}
            </div>

            {/* Footer Section */}
            <div 
              className="border-t border-gray-200 mt-auto flex-shrink-0"
              style={{ padding: `var(--drawer-padding)` }}
            >
              <div 
                className="text-center flex flex-col"
                style={{ gap: `var(--drawer-spacing)` }}
              >
                {/* Currency Selector */}
                <div 
                  className="pointer-events-auto"
                  style={{ paddingTop: `var(--drawer-spacing)` }}
                >
                  <div 
                    className="uppercase tracking-wider text-gray-500"
                    style={{ 
                      fontSize: `max(0.4rem, min(0.625rem, ${window.innerHeight * 0.01}px))`,
                      marginBottom: `var(--drawer-spacing)`
                    }}
                  >
                    Currency
                  </div>
                  <div className="flex justify-center">
                    <CurrencySelector />
                  </div>
                </div>
                
                {/* Social Links */}
                <div style={{ paddingTop: `var(--drawer-spacing)` }}>
                  <div 
                    className="uppercase tracking-wider text-gray-500"
                    style={{ 
                      fontSize: `max(0.4rem, min(0.625rem, ${window.innerHeight * 0.01}px))`,
                      marginBottom: `var(--drawer-spacing)`
                    }}
                  >
                    Connect With Us
                  </div>
                  <div 
                    className="flex items-center justify-center"
                    style={{ gap: `var(--drawer-spacing)` }}
                  >
                    <a 
                      href="https://instagram.com/jampackdcarepackages" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-gray-500 hover:text-jamaica-green transition-colors rounded-full hover:bg-jamaica-green/5"
                      style={{ padding: `var(--drawer-spacing)` }}
                    >
                      <Instagram 
                        style={{
                          width: `max(0.8rem, min(1.25rem, ${window.innerHeight * 0.016}px))`,
                          height: `max(0.8rem, min(1.25rem, ${window.innerHeight * 0.016}px))`
                        }}
                      />
                    </a>
                    <a 
                      href="https://tiktok.com/@jampackdcarepackages" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-gray-500 hover:text-jamaica-green transition-colors rounded-full hover:bg-jamaica-green/5"
                      style={{ padding: `var(--drawer-spacing)` }}
                    >
                      <FaTiktok 
                        style={{
                          width: `max(0.8rem, min(1.25rem, ${window.innerHeight * 0.016}px))`,
                          height: `max(0.8rem, min(1.25rem, ${window.innerHeight * 0.016}px))`
                        }}
                      />
                    </a>
                    <a 
                      href="https://wa.me/18763276630?text=Hi%20Paul%21%20I%27d%20appreciate%20your%20help%20with%20something" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-gray-500 hover:text-jamaica-green transition-colors rounded-full hover:bg-jamaica-green/5"
                      style={{ padding: `var(--drawer-spacing)` }}
                    >
                      <FaWhatsapp 
                        style={{
                          width: `max(0.8rem, min(1.25rem, ${window.innerHeight * 0.016}px))`,
                          height: `max(0.8rem, min(1.25rem, ${window.innerHeight * 0.016}px))`
                        }}
                      />
                    </a>
                  </div>
                </div>

                {/* Version & Attribution */}
                <div 
                  className="text-center text-gray-500"
                  style={{ 
                    paddingTop: `var(--drawer-spacing)`,
                    fontSize: `max(0.45rem, min(0.75rem, ${window.innerHeight * 0.011}px)`
                  }}
                >
                  <div>Version 1.3.10</div>
                  <div className="text-gray-400">
                    Developed by {" "}
                    <a
                      href="https://paulsmith.codes"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-jamaica-green hover:text-jamaica-green/80 font-medium transition-colors duration-200 inline-flex items-center gap-1"
                    >
                      PaulSmith.Codes 
                      <ExternalLink 
                        style={{
                          width: `max(0.4rem, min(0.75rem, ${window.innerHeight * 0.01}px))`,
                          height: `max(0.4rem, min(0.75rem, ${window.innerHeight * 0.01}px))`
                        }}
                      />
                    </a>
                  </div>
                </div>

                {/* Rights reserved */}
                <div 
                  className="text-gray-500"
                  style={{ 
                    fontSize: `max(0.4rem, min(0.625rem, ${window.innerHeight * 0.009}px))`,
                    marginTop: `var(--drawer-spacing)`,
                    marginBottom: `calc(var(--drawer-spacing) * 0.5)`
                  }}
                >
                  &copy; {new Date().getFullYear()} JamPack'd Care Packages. All rights reserved.
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      )}
    </>
  );
};

export default NavBar;
